import React, { useState, useCallback, useEffect } from 'react';
import {
  Box as MuiBox,
  List as MuiList,
  ListItem as MuiListItem,
  ListItemButton as MuiListItemButton,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  Divider as MuiDivider,
  Typography as MuiTypography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DragIndicator as DragIcon } from '@mui/icons-material';

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  dividerAfter?: boolean;
}

interface ColumnNavigationProps {
  /** Navigation items to display */
  items: NavigationItem[];
  /** Currently selected item ID */
  selectedId?: string;
  /** Callback when item is selected */
  onItemSelect: (itemId: string) => void;
  /** Whether the navigation is resizable */
  resizable?: boolean;
  /** Minimum width for resizing (default: 240px) */
  minWidth?: number;
  /** Maximum width for resizing (default: 360px) */
  maxWidth?: number;
  /** Initial width (default: 280px) */
  initialWidth?: number;
  /** Storage key for persisting width */
  storageKey?: string;
  /** Optional header content */
  header?: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Additional sx styling */
  sx?: any;
}

export const ColumnNavigation: React.FC<ColumnNavigationProps> = ({
  items,
  selectedId,
  onItemSelect,
  resizable = false,
  minWidth = 240,
  maxWidth = 360,
  initialWidth = 280,
  storageKey,
  header,
  footer,
  sx,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Width state management
  const [navWidth, setNavWidth] = useState(() => {
    if (typeof window !== 'undefined' && storageKey) {
      const saved = localStorage.getItem(storageKey);
      return saved ? parseInt(saved, 10) : initialWidth;
    }
    return initialWidth;
  });
  const [isResizing, setIsResizing] = useState(false);

  // Save width to localStorage
  useEffect(() => {
    if (storageKey && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, navWidth.toString());
    }
  }, [navWidth, storageKey]);

  // Resize handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!resizable) return;
    e.preventDefault();
    setIsResizing(true);
  }, [resizable]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const newWidth = e.clientX - 80; // Account for 80px navigation rail
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setNavWidth(newWidth);
    }
  }, [isResizing, minWidth, maxWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <MuiBox
      sx={{
        width: navWidth,
        height: '100%',
        position: 'sticky',
        top: 0,
        bgcolor: '#F1F5FE', // Consistent background color
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      {/* Optional Header */}
      {header && (
        <MuiBox
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {header}
        </MuiBox>
      )}

      {/* Navigation List */}
      <MuiBox sx={{ flex: 1, overflow: 'auto' }}>
        <MuiList sx={{ p: 0 }}>
          {items.map((item, index) => (
            <React.Fragment key={item.id}>
              <MuiListItem disablePadding>
                <MuiListItemButton
                  selected={selectedId === item.id}
                  onClick={() => onItemSelect(item.id)}
                  sx={{
                    px: 3, // 24px horizontal padding to match WorkProductEditor
                    py: 1.5, // 12px vertical padding to match WorkProductEditor
                    minHeight: 48, // Minimum touch target for accessibility
                    '&.Mui-selected': {
                      bgcolor: 'primary.main', // Blue background like WorkProductEditor
                      color: 'primary.contrastText', // White text on blue background
                      '&:hover': {
                        bgcolor: 'primary.dark', // Darker blue on hover when selected
                      }
                    },
                    '&:hover': {
                      bgcolor: 'action.hover', // Gray background on hover when not selected
                    },
                    position: 'relative', // For the active indicator positioning
                    // Ensure consistent typography
                    '& .MuiListItemText-primary': {
                      fontSize: '0.875rem', // 14px base font size
                      fontWeight: selectedId === item.id ? 600 : 400,
                      lineHeight: 1.5,
                      color: 'inherit', // Inherit from parent for proper contrast
                    },
                  }}
                >
                  <MuiListItemIcon
                    sx={{
                      minWidth: 40, // MUI spacing-5 (40px)
                      color: selectedId === item.id ? '#FFFFFF' : 'text.secondary', // Explicit white for selected items
                      '& .MuiSvgIcon-root': {
                        fontSize: 20, // Consistent icon size (20px)
                        color: selectedId === item.id ? '#FFFFFF' : 'text.secondary', // Explicit white for selected, gray for non-selected
                      },
                      '& svg': {
                        color: selectedId === item.id ? '#FFFFFF' : 'text.secondary', // For custom SVG icons
                      },
                    }}
                  >
                    {item.icon}
                  </MuiListItemIcon>
                  <MuiListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '0.875rem', // 14px consistent with global CSS
                        fontWeight: selectedId === item.id ? 600 : 400,
                        lineHeight: 1.5,
                        color: 'inherit', // Inherit color from parent for proper contrast
                      },
                    }}
                  />
                  {/* Active indicator - matches WorkProductEditor style */}
                  {selectedId === item.id && (
                    <MuiBox
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3, // 3px width like WorkProductEditor
                        bgcolor: 'secondary.main', // Secondary color for the indicator
                      }}
                    />
                  )}
                </MuiListItemButton>
              </MuiListItem>
              {/* Conditional divider after specific items */}
              {item.dividerAfter && (
                <MuiDivider sx={{ mx: 2, my: 1 }} />
              )}
            </React.Fragment>
          ))}
        </MuiList>
      </MuiBox>

      {/* Optional Footer */}
      {footer && (
        <MuiBox
          sx={{
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          {footer}
        </MuiBox>
      )}

      {/* Resize Handle */}
      {resizable && !isMobile && (
        <MuiBox
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            top: 0,
            right: -1,
            width: 8, // MUI spacing unit (8px)
            height: '100%',
            cursor: 'col-resize',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.2s',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              width: 1, // 1px border
              height: '100%',
              bgcolor: 'divider',
            },
            '&:hover': {
              opacity: 1,
              bgcolor: 'action.hover',
            },
          }}
        >
          <DragIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        </MuiBox>
      )}
    </MuiBox>
  );
};

export default ColumnNavigation;